import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Offcanvas } from 'bootstrap';
import logo from "./../../assets/images/logo-win.svg";
import { useTranslation } from 'react-i18next';
import ChatBotWindow from "../ChatBot/ChatBotWindow";
import { useSelector } from "react-redux";
import "../Layout/chatfloat.css";
import { sub } from "date-fns";

const Header = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMeExpanded, setMeExpanded] = useState(false);
  const [isChatOpen, setChatOpen] = useState(false);
  const [logo, setLogo] = useState(null);
  const [activeMenu, setActiveMenu] = useState(""); // Track active menu, default to none selected
  const [activeSubMenu, setActiveSubMenu] = useState(""); // Add state for tracking active submenu
  const [isMeMobileOpen, setIsMeMobileOpen] = useState(false);
  const [editProfileMobileOpen, setEditProfileMobileOpen] = useState(false); // New state for edit profile submenu
  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const userData = useSelector((state) => state.userData); // Assuming user data is in Redux state
  const [isAffirmative, setIsAffirmative] = useState(
    userData?.userData?.IsAffirmative === "Y" ||
    userData?.registration?.registrationData?.IsAffirmative === "Y"
  );
  const [isBenefitsTabVisible, setIsBenefitsTabVisible] = useState(localStorage.getItem('BenefitTabVisibility') === 'Y');
  const [insuranceCardVisible, setInsuranceCardVisible] = useState(localStorage.getItem('InsuranceCardVisibility') === 'Y');
  // Show notification tab only when NotificationIcon === "Y" in either userData or registrationData
  const [isNotificationVisible, setIsNotificationVisible] = useState(isHighmarkPortal ? true : (
    userData?.userData?.NotificationIcon === "Y" ||
    userData?.registration?.registrationData?.NotificationIcon === "Y"
  ));

  useEffect(() => {
    setIsNotificationVisible(isHighmarkPortal ? true : (
      userData?.userData?.NotificationIcon === "Y" ||
      userData?.registration?.registrationData?.NotificationIcon === "Y"
    ));
  }, [userData, isHighmarkPortal]);

  useEffect(() => {
    setIsBenefitsTabVisible(
        localStorage.getItem('BenefitTabVisibility') === 'Y' ||
        userData?.userData?.BenefitTabVisibility === 'Y' ||
        userData?.registration?.registrationData?.BenefitTabVisibility === 'Y'
    );
    setInsuranceCardVisible(
        localStorage.getItem('InsuranceCardVisibility') === 'Y' ||
        userData?.userData?.InsuranceCardVisibility === 'Y' ||
        userData?.registration?.registrationData?.InsuranceCardVisibility === 'Y'
    );
    setIsAffirmative(
      userData?.userData?.IsAffirmative === "Y" ||
      userData?.registration?.registrationData?.IsAffirmative === "Y"
    );
  }, [userData]);

  const meDropdownRef = useRef(null);

  // Determine if WINcentive should be shown instead of Support
  const whyHereRaw =
    userData?.registration?.registrationData?.WhyHere ||
    userData?.userData?.WhyHere || '';
    const showWincentive = userData?.userData?.WINCentive === "Y";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (meDropdownRef.current && !meDropdownRef.current.contains(event.target)) {
        // setActiveMenu(""); // Always close the menu when clicking outside
        if (activeMenu === "me") setActiveMenu("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeMenu, activeSubMenu]);

  useEffect(() => {

    const loadLogo = async () => {
      if (isHighmarkPortal) {
        const logoModule = await import("./../../assets/images/highmark/ffb-logo.jpg");
        setLogo(logoModule.default);
      } else {
        const logoModule = await import("./../../assets/images/win/logo-win.svg");
        setLogo(logoModule.default);
      }
    };

    loadLogo();
  }, [ssoData]);

  useEffect(() => {
    const resetMenu = () => {
      setActiveMenu("");
      setActiveSubMenu("");
    };
    window.addEventListener("resetHeaderMenu", resetMenu);
    return () => window.removeEventListener("resetHeaderMenu", resetMenu);
  }, []);
  
  // Function to close the offcanvas menu and remove all backdrops
  const closeOffcanvasMenu = () => {
    const offcanvasMenu = document.getElementById('offcanvasMenu');
    if (offcanvasMenu) {
      const bsOffcanvas = Offcanvas.getInstance(offcanvasMenu) || new Offcanvas(offcanvasMenu);
      bsOffcanvas.hide();

      // Remove any lingering backdrops
      setTimeout(() => {
        document.querySelectorAll('.offcanvas-backdrop').forEach(backdrop => backdrop.remove());
      }, 300); // Adjust delay to match offcanvas transition
    }
  };

  // Update navigateAndClose to handle mobile menu state
  const navigateAndClose = (path, menu, submenu = "") => {
    setIsMeMobileOpen(false);
    setEditProfileMobileOpen(false);
    navigate(path);
    if (submenu) {
      if((submenu==="privacy" || submenu==="terms")&& !isHighmarkPortal){
        setActiveSubMenu("");
        setActiveMenu(menu);
      }
      else{
      setActiveMenu(""); // Close the menu after navigation
      setActiveSubMenu(submenu); // Keep track of which submenu is active

      }
    } else if (menu === "me") {

      // For main "Me" menu items
      setActiveMenu("");
      setActiveSubMenu("");
    } else {
      // For other main menu items
      setActiveMenu(menu);
      setActiveSubMenu("");
    }
    closeOffcanvasMenu();

  };

  // Toggle "Me" submenu in offcanvas
  const toggleMeMenu = () => {
    setMeExpanded(!isMeExpanded);
  };

  const toggleChat = () => {
    setChatOpen(!isChatOpen); // Toggle chat visibility
  };

  useEffect(() => {
    const staticSubMenus = {
      "/privacypolicy": "privacy",
      "/TermsAndConditions": "terms",
      "/contact-us": "contact",
      "/updatephonenumber": "update-phone",
      "/edit-healthprofilesetup": "update-profile",
    };

    const submenu = staticSubMenus[pathname];

    if (submenu) {
      setActiveMenu("");
      setActiveSubMenu(submenu);
    } else {
      window.dispatchEvent(new Event("resetHeaderMenu"));
    }
  }, [pathname]);

  // Helper to close Me menu and navigate
  const handleMeMenuNavigate = (path, submenu) => {
    navigateAndClose(path, "", submenu);
  };

  // Handle mobile Me menu toggle
  const handleMobileMenuToggle = (e) => {
    e.preventDefault();
    setActiveMenu(prev => prev === "me" ? "" : "me");
    setIsMeMobileOpen(prev => {
      if (!prev) {
        setEditProfileMobileOpen(false); // Reset submenu when opening
      }
      return !prev;
    });
  };

  // Handle mobile submenu toggle
  const handleMobileSubmenuToggle = (e) => {
    e.preventDefault();
    if (isMeMobileOpen) {
      setEditProfileMobileOpen(prev => !prev);
    }
  };

  // Function to handle navigation and menu state
  const handleMainMenuClick = (menuName, path) => {
    setActiveMenu(menuName);
    setActiveSubMenu(""); // Clear any active submenu
    setIsMeMobileOpen(false); // Close mobile menu if open
    navigate(path);
    closeOffcanvasMenu(); // Close mobile menu drawer if open
    
  };
// put these inside your component
const collapseMe = () => {
  setIsMeMobileOpen(false);
  setEditProfileMobileOpen(false);
  setActiveSubMenu("");
  if (activeMenu === "me") setActiveMenu("");
};

const onMobileNavClick = () => {
  collapseMe();
  closeOffcanvasMenu();
};
  return (
    <>
      <header>
        <nav className="navbar navbar-expand-lg container-fixed custom-navbar">
          {isHighmarkPortal ? (
            <>
              <button
                className="navbar-toggler"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  const offcanvasMenu = document.getElementById('offcanvasMenu');
                  if (offcanvasMenu) {
                    const bsOffcanvas = Offcanvas.getInstance(offcanvasMenu) || new Offcanvas(offcanvasMenu);
                    bsOffcanvas.show();
                  }
                }}
                aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon"></span>
              </button>

              <Link
                to="/home"
                className="navbar-brand"
                onClick={() => {
                  setActiveMenu("home");
                  closeOffcanvasMenu();
                  setIsMeMobileOpen(false);
                  setEditProfileMobileOpen(false);
                  
                }}
              >
                <img src={logo} alt="Logo" className="header-logo" />
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/home"
                className="navbar-brand"
                onClick={() => {
                  setActiveMenu("home");
                  closeOffcanvasMenu();
                  setIsMeMobileOpen(false);
                  setEditProfileMobileOpen(false);
                }}
              >
                <img src={logo} alt="Logo" className="header-logo" />
              </Link>
              <button
                className="navbar-toggler"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  const offcanvasMenu = document.getElementById('offcanvasMenu');
                  if (offcanvasMenu) {
                    const bsOffcanvas = Offcanvas.getInstance(offcanvasMenu) || new Offcanvas(offcanvasMenu);
                    bsOffcanvas.show();
                  }
                }}
                aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon"></span>
              </button>
            </>
          )}

          <div className="collapse navbar-collapse justify-content-end d-none d-lg-block">
            <ul className="navbar-nav mb-2 mb-lg-0">
              <li className="nav-item"><a className={`nav-link${pathname === "/home" ? " active" : ""}`} onClick={() => handleMainMenuClick("home", "/home")} >{t('home')}</a></li>
              <li className="nav-item">
                {showWincentive ? (
                  <a className={`nav-link${pathname === "/incentive-landingpage" ? " active" : ""}`} onClick={() => handleMainMenuClick("Rewards", "/incentive-landingpage")}>Rewards</a>
                ) : (
                  <a className={`nav-link${pathname === "/allsupport-services" ? " active" : ""}`} onClick={() => handleMainMenuClick("support", "/allsupport-services")}>{t('support')}</a>
                )}
              </li>
              <li className="nav-item"><a className={`nav-link${pathname === "/find-care" ? " active" : ""}`} onClick={() => handleMainMenuClick("findcare", "/find-care")} >{t('findcare')}</a></li>
              {isBenefitsTabVisible && (
                <li className="nav-item"><a className={`nav-link${pathname === "/benefits-landingpage" ? " active" : ""}`} onClick={() => handleMainMenuClick("benefits", "/benefits-landingpage")} >{t('benefits')}</a></li>
              )}
              <li className="nav-item dropdown" ref={meDropdownRef}>
                <a
                  className={`nav-link dropdown-toggle${activeMenu === "me" || activeSubMenu ? " active" : ""}`}
                  
                  role="button"
                  aria-expanded={activeMenu === "me"}
                  onClick={e => {
                    e.preventDefault();
                    if (activeMenu === "me") {
                      setActiveMenu(""); // Close menu if it's open
                      if (!activeSubMenu) {
                        setActiveSubMenu(""); // Only clear submenu if none is active
                      }
                    } else {
                      setActiveMenu("me"); // Open menu
                    }
                  }}
                >
                  {t('me')}
                </a>
                {activeMenu === "me" && (
                  <ul className="dropdown-menu win-dropdown-menu show" style={{ display: 'block' }}>
                    {!isHighmarkPortal && (
                      <li className="dropdown-submenu win-dropdown-menu-item">
                        <a className={`dropdown-item dropdown-toggle${activeSubMenu === "accsetting" ? " active" : ""}`} >{t('accsetting')}</a>
                        <ul className="dropdown-menu">
                          {!isHighmarkPortal && (
                            <li>
                              <a className={`dropdown-item${activeSubMenu === "reset-password" ? " active" : ""}`}
                                
                                onClick={() => handleMeMenuNavigate("/change-password", "reset-password")}>{t('resetpwd')}</a>
                            </li>
                          )}
                          <li>
                            <a className={`dropdown-item${activeSubMenu === "notification" ? " active" : ""}`}
                              
                              onClick={() => handleMeMenuNavigate("/notification-preference", "notification")}>{t('notification')}</a>
                          </li>
                        </ul>
                      </li>
                    )}
                    <li className="dropdown-submenu win-dropdown-menu-item">
                      <a className={`dropdown-item dropdown-toggle${activeSubMenu === "editprofile" ? " active" : ""}`} >{t('editprofile')}</a>
                      <ul className="dropdown-menu">
                        <li>
                          <a className={`dropdown-item${activeSubMenu === "update-phone" ? " active" : ""}`}
                            
                            onClick={() => handleMeMenuNavigate("/updatephonenumber", "update-phone")}>{t('updatephone')}</a>
                        </li>
                        <li>
                          <a className={`dropdown-item${activeSubMenu === "update-profile" ? " active" : ""}`}
                            
                            onClick={() => handleMeMenuNavigate("/edit-healthprofilesetup", "update-profile")}>{t('updateprofileinfo')}</a>
                        </li>
                      </ul>
                    </li>
                    {!isHighmarkPortal && insuranceCardVisible && (
                      <li className="win-dropdown-menu-item">
                        <a className={`dropdown-item win-dropdown-meni-item${activeSubMenu === "insurance" ? " active" : ""}`}
                          
                          onClick={() => handleMeMenuNavigate("/insurenceupload", "insurance")}>{t('uploadhealthcard')}</a>
                      </li>
                    )}
                    <li className="win-dropdown-menu-item">
                      <a className={`dropdown-item win-dropdown-meni-item${activeSubMenu === "contact" ? " active" : ""}`}
                        
                        onClick={() => handleMeMenuNavigate("/contact-us", "contact")}>{t('contact')}</a>
                    </li>
                    <li className="win-dropdown-menu-item">
                      <a className={`dropdown-item win-dropdown-meni-item${activeSubMenu === "privacy" ? " active" : ""}`}
                        
                        onClick={() => handleMeMenuNavigate('/privacypolicy', "privacy")}>{t('privacypolicy')}</a>
                    </li>
                    <li className="win-dropdown-menu-item">
                      <a className={`dropdown-item win-dropdown-meni-item${activeSubMenu === "terms" ? " active" : ""}`}
                        
                        onClick={() => handleMeMenuNavigate('/TermsAndConditions', "terms")}>{t('conditions')}</a>
                    </li>
                    {isAffirmative && (
                      <li className={`win-dropdown-menu-item${activeSubMenu === "affirmative" ? " active" : ""}`}>
                        <a className={`dropdown-item win-dropdown-meni-item${activeSubMenu === "affirmative" ? " active" : ""}`}
                          href="#"
                          onClick={() => {
                            setActiveMenu("me");
                            handleMeMenuNavigate("/affirmative-statement", "affirmative");
                          }}
                        >
                          Affirmative Statement
                        </a>
                      </li>
                    )}
                    <li className="win-dropdown-menu-item">
                      <a className={`dropdown-item win-dropdown-meni-item${activeSubMenu === "logout" ? " active" : ""}`}
                        
                        onClick={() => handleMeMenuNavigate('/Logout', "logout")}>{t('logoutt')}</a>
                    </li>
                  </ul>
                )}
              </li>
              {isNotificationVisible && (
                <li className="nav-item">
                  <a className={`nav-link nav-btn-notification${pathname === "/notifications" ? " active" : ""}`}
                    onClick={() => handleMainMenuClick("notifications", "/notifications")} >
                    <span></span>
                  </a>
                </li>
              )}
            </ul>
            {isChatOpen && (
              <div className="chat-modal-overlay">
                <div className="chat-modal">
                  <button className="close-button" onClick={toggleChat}>×</button>
                  <ChatBotWindow />
                </div>
              </div>
            )}
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <div className="offcanvas offcanvas-start d-lg-none" tabIndex="-1" id="offcanvasMenu" aria-labelledby="offcanvasMenuLabel">
        <div className="offcanvas-header">
          <h5 className="offcanvas-title" id="offcanvasMenuLabel">
            <img src={logo} alt="Logo" className="header-logo" />
          </h5>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>
        <div className="offcanvas-body">
          <ul className="navbar-nav">
            <li className="nav-item">
              <Link
                to="/home"
                className={`nav-link${pathname === "/home" ? " active" : ""}`}
                onClick={onMobileNavClick}
              >
                {t('home')}
              </Link>
            </li>
            <li className="nav-item">
              {showWincentive ? (
                <Link
                  to="/incentive-landingpage"
                  className={`nav-link${pathname === "/incentive-landingpage" ? " active" : ""}`}
                  onClick={onMobileNavClick}
                >
                  Rewards
                </Link>
              ) : (
                <Link
                  to="/allsupport-services"
                  className={`nav-link${pathname === "/allsupport-services" ? " active" : ""}`}
                  onClick={onMobileNavClick}
                >
                  {t('support')}
                </Link>
              )}
            </li>
            <li className="nav-item">
              <Link
                to="/find-care"
                className={`nav-link${pathname === "/find-care" ? " active" : ""}`}
                onClick={onMobileNavClick}
              >
                {t('findcare')}
              </Link>
            </li>
            {isBenefitsTabVisible && (
              <li className="nav-item">
                <Link
                  to="/benefits-landingpage"
                  className={`nav-link${pathname === "/benefits-landingpage" ? " active" : ""}`}
                  onClick={onMobileNavClick}
                >
                  {t('benefits')}
                </Link>
              </li>
            )}
            <li className="nav-item">
              <a
                className={`nav-link dropdown-toggle${activeMenu === "me" || activeSubMenu ? " active" : ""}`}
                
                role="button"
                aria-expanded={isMeMobileOpen}
                onClick={handleMobileMenuToggle}
              >
                {t('me')}
              </a>
              <ul
                className={`dropdown-menu me-dropdown-items${isMeMobileOpen ? " show" : ""}`}
                style={{ display: isMeMobileOpen ? 'block' : 'none' }}
              >
                {!isHighmarkPortal && (
                  <li className="dropdown-submenu">
                    <a className={`dropdown-item dropdown-toggle me-level2-dropdown${activeSubMenu === "accsetting" ? " active" : ""}`}
                      
                      onClick={handleMobileSubmenuToggle}>
                      {t('accsetting')}
                    </a>
                    <ul className="me-level2-dropdown-items">
                      <li>
                        <a className={`dropdown-item${activeSubMenu === "reset-password" ? " active" : ""}`}
                          
                          onClick={() => handleMeMenuNavigate("/change-password", "reset-password")}>
                          {t('resetpwd')}
                        </a>
                      </li>
                      <li>
                        <a className={`dropdown-item${activeSubMenu === "notification" ? " active" : ""}`}
                          
                          onClick={() => handleMeMenuNavigate("/notification-preference", "notification")}>
                          {t('notification')}
                        </a>
                      </li>
                    </ul>
                  </li>
                )}
                <li className="dropdown-submenu">
                  <a className={`dropdown-item dropdown-toggle me-level2-dropdown${activeSubMenu === "editprofile" ? " active" : ""}`}
                    
                    onClick={handleMobileSubmenuToggle}>
                    {t('editprofile')}
                  </a>
                  <ul className="me-level2-dropdown-items" style={{ display: isMeMobileOpen && editProfileMobileOpen ? 'block' : 'none' }}>
                    <li>
                      <a className={`dropdown-item${activeSubMenu === "update-phone" ? " active" : ""}`}
                        
                        onClick={() => handleMeMenuNavigate("/updatephonenumber", "update-phone")}>
                        {t('updatephone')}
                      </a>
                    </li>
                    <li>
                      <a className={`dropdown-item${activeSubMenu === "update-profile" ? " active" : ""}`}
                        
                        onClick={() => handleMeMenuNavigate("/edit-healthprofilesetup", "update-profile")}>
                        {t('updateprofileinfo')}
                      </a>
                    </li>
                  </ul>
                </li>
                {!isHighmarkPortal && (
                  <li>
                    <a className={`dropdown-item${activeSubMenu === "insurance" ? " active" : ""}`}
                      
                      onClick={() => handleMeMenuNavigate("/insurenceupload", "insurance")}>
                      {t('uploadhealthcard')}
                    </a>
                  </li>
                )}
                <li>
                  <a className={`dropdown-item${activeSubMenu === "contact" ? " active" : ""}`}
                    
                    onClick={() => handleMeMenuNavigate("/contact-us", "contact")}>
                    {t('contact')}
                  </a>
                </li>
                <li>
                  <a 
                  className={`dropdown-item${activeSubMenu === "privacy" ? " active" : ""}`}
                    
                    onClick={() => handleMeMenuNavigate('/privacypolicy', "privacy")}>
                    {t('privacypolicy')}
                  </a>
                </li>
                <li>
                  <a className={`dropdown-item${activeSubMenu === "terms" ? " active" : ""}`}
                    
                    onClick={() => handleMeMenuNavigate('/TermsAndConditions', "terms")}>
                    {t('conditions')}
                  </a>
                </li>
                {isAffirmative && (
                  <li>
                    <a className={`dropdown-item${activeSubMenu === "affirmative" ? " active" : ""}`}
                      href="#"
                      onClick={() => handleMeMenuNavigate("/affirmative-statement", "affirmative")}
                    >
                      Affirmative Statement
                    </a>
                  </li>
                )}
                <li>
                  <a className={`dropdown-item${activeSubMenu === "logout" ? " active" : ""}`}
                    
                    onClick={() => handleMeMenuNavigate('/Logout', "logout")}>
                    {t('logoutt')}
                  </a>
                </li>
              </ul>
            </li>
            {isNotificationVisible && (
              <li className="nav-item d-flex justify-content-between">
                <Link
                  to="/notifications"
                  className={`nav-link${pathname === "/notifications" ? " active" : ""}`}
                  onClick={onMobileNavClick}
                >
                  {t('notifications')}
                </Link>
              </li>
            )}
          </ul>
        </div>
      </div>
    </>
  );
}

export default Header;
